import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { z } from "zod"
import { getErrorCode } from "@krakentech/blueprint-api"
import { type InputOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { logger } from "@/lib/logger"
import { MeterPowerAnalysisRouteNames } from "@/routes/meterPowerAnalysis"
import { SuggestedPowerAdjustmentSchema } from "@/scalars/oebackend/suggestedPowerAdjustment"
import { executePromiseWithDelay } from "@/utils/executePromiseWithDelay"
import { PMAX_ERRORS_CODES } from "../constants/pmaxErrorsCodes"
import { RunMeterPointPmaxAnalysisMutation } from "../graphql/runMeterPointPmaxAnalysis"
import { type PowerAnalysisValues } from "../types/powerAnalysisValues"
import { isNotPmaxEligibleError } from "../utils/isNotPmaxEligibleError"
import { isPmaxOverloadedError } from "../utils/isPmaxOverloadedError"
import { useMeterPowerAnalysisWizard } from "../wizard"

const RunMeterPointPmaxAnalysisSchema = z
    .object({
        pmaxAnalysis: z.object({
            token: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            currentSubscribedMaxPower: z.number(),
            recommendedSubscribedMaxPower: z.number().nullable(),
            suggestedPowerAdjustment: SuggestedPowerAdjustmentSchema,
            yearlyProfitAmountCents: z.number().nullable(),
        }),
    })
    .transform((data) => data.pmaxAnalysis)

export function useRunMeterPointPmaxAnalysis() {
    const [isLoading, setIsLoading] = useState(false)
    const { formValues, updateFormValues, toNextStep, toStep } = useMeterPowerAnalysisWizard()
    const oeGraphQLClient = useGraphQLClient({ target: "oeBackend" })

    const overloadRedirection = () => {
        updateFormValues({ pmaxToken: null, errorCode: "OVERLOAD" })

        return toStep(MeterPowerAnalysisRouteNames.Overload)
    }

    const errorRedirection = ({
        errorCode = "UNKNOWN",
        pmaxToken = null,
    }: { errorCode?: string; pmaxToken?: string | null } = {}) => {
        updateFormValues({ pmaxToken, errorCode })

        return toStep(MeterPowerAnalysisRouteNames.Error, { errorCode })
    }

    const meterPointPmaxAnalysis = useMutation({
        mutationFn: async (input: InputOf<typeof RunMeterPointPmaxAnalysisMutation>) =>
            await oeGraphQLClient.request(RunMeterPointPmaxAnalysisMutation, { input }),
        onSuccess: (data) => {
            const pmaxAnalysis = RunMeterPointPmaxAnalysisSchema.safeParse(
                data.runMeterPointPmaxAnalysis
            )

            if (!pmaxAnalysis.success) return errorRedirection()

            const {
                token,
                suggestedPowerAdjustment,
                currentSubscribedMaxPower,
                recommendedSubscribedMaxPower,
                yearlyProfitAmountCents,
            } = pmaxAnalysis.data

            // This prevent the user to go to the result step if the requested values are not present
            const hasAdjustmentValues = !!recommendedSubscribedMaxPower && !!yearlyProfitAmountCents

            if (suggestedPowerAdjustment === "NONE") {
                updateFormValues({ pmaxToken: token, currentSubscribedMaxPower })

                return toStep(MeterPowerAnalysisRouteNames.Adapted)
            }

            if (!hasAdjustmentValues || suggestedPowerAdjustment === "HIGHER") {
                return errorRedirection({ pmaxToken: token })
            }

            updateFormValues({
                pmaxToken: token,
                currentSubscribedMaxPower,
                recommendedSubscribedMaxPower,
                yearlyProfitAmountCents,
            })

            return toNextStep()
        },
        onError: async (error) => {
            const errorCode = getErrorCode(error)

            if (error && errorCode && isPmaxOverloadedError(errorCode)) {
                return overloadRedirection()
            } else if (error && errorCode && isNotPmaxEligibleError(errorCode)) {
                return errorRedirection({ errorCode })
            }

            const meta = {
                feature: "meterPowerAnalysis",
                source: "useRunMeterPointPmaxAnalysis",
            }

            const isBusinessError = !!errorCode && PMAX_ERRORS_CODES.includes(errorCode)

            if (isBusinessError) {
                logger.info({ error, meta })
            } else {
                logger.error({ error, meta })
            }

            return errorRedirection()
        },
    })

    async function handleRunMeterPointPmaxAnalysis(values: PowerAnalysisValues) {
        const { meterPointId, user } = values
        const { firstName, lastName } = user
        const { productCode } = formValues

        updateFormValues({
            meterPointId,
            user: { ...formValues.user, firstName, lastName },
        })

        setIsLoading(true)

        const input = {
            meterPointId,
            firstName: user.firstName,
            lastName: user.lastName,
            industryConsent: true,
            currentOccupierConsent: true,
            ...(productCode ? { productCode } : {}),
        }

        await executePromiseWithDelay(() => meterPointPmaxAnalysis.mutateAsync(input), 2000)

        setIsLoading(false)
    }

    return [
        handleRunMeterPointPmaxAnalysis,
        {
            error: meterPointPmaxAnalysis.error,
            loading: meterPointPmaxAnalysis.isPending || isLoading,
        },
    ] as const
}
